'use client';

import ImageOptimizer from '@/components/image-optimizer';
import { ClassInfoData } from '@/data/academy.price.data';

interface ClassInfoProps {
  data: ClassInfoData;
}

export default function ClassInfo({ data }: ClassInfoProps) {
  const { title, summary, steps, preparation } = data;

  return (
    <section className=" bg-white">
      <div className="px-5 pt-8 pb-5">
        <h2 className="typo-20 font-extrabold text-222222 mb-4">{title}</h2>

        <div className="bg-[#F5F5FA] rounded-lg py-4 mb-3 text-333333 font-bold typo-14 text-center">
          <p className="mb-2">{summary.timeBreakdown}</p>
          <p>
            {summary.totalHours.map((part, index) =>
              part.highlight ? (
                <span key={index} className="text-[#FB4040]">
                  {part.text}
                </span>
              ) : (
                <span key={index}>{part.text}</span>
              ),
            )}
          </p>
        </div>
        <ul className="space-y-2 mb-5">
          {summary.memo?.map((item, index) => (
            <li key={index} className="typo-12 font-medium text-[#4F4F5E]">
              <span className="shrink-0">*</span> {item}
            </li>
          ))}
        </ul>

        <div className="space-y-6">
          {steps.map((step) => (
            <div key={step.stepNumber} className="flex flex-col gap-4">
              <div className="flex items-center gap-2 ">
                <span className="rounded-[3px] px-2 h-[20px] flex items-center justify-center typo-12 font-semibold text-333333 bg-[#FFD42F]">
                  Step {String(step.stepNumber).padStart(2, '0')}
                </span>
                <h3 className="typo-16 font-extrabold text-222222">
                  {step.title}
                </h3>
              </div>

              <div className="relative aspect-[320/120] w-full">
                <ImageOptimizer
                  src={step.image}
                  alt={step.title}
                  width={320}
                  height={120}
                  className="w-full h-auto object-cover"
                />
              </div>

              <p className="typo-14 font-bold text-[#4F4F5E] leading-normal">
                {step.description}
              </p>

              <ul className="space-y-2">
                {step.items.map((item, index) => (
                  <li
                    key={`step-${step.stepNumber}-item-${index}`}
                    className="typo-13 font-semibold text-[#908E9E]"
                  >
                    <span className="shrink-0">·</span> {item.label}
                    {' : '}
                    {item.value}
                  </li>
                ))}
              </ul>

              {step.testItems && (
                <div className="flex flex-col gap-3">
                  <h4 className="typo-14 font-bold text-222222">
                    {step.testItems.title}
                  </h4>
                  <div className="p-4 bg-[#F5F5FA] rounded-sm">
                    <ul className="space-y-1.5">
                      {step.testItems.items.map((item, index) => (
                        <li
                          key={`step-${step.stepNumber}-test-${index}`}
                          className="typo-13 font-semibold text-[#4F4F5E] leading-snug"
                        >
                          {typeof item === 'string' ? (
                            item
                          ) : (
                            <div className="flex items-start">
                              <span className="shrink-0">
                                ·&nbsp;{item.title}&nbsp;:&nbsp;
                              </span>
                              <span>{item.desc}</span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="h-[2px] bg-efeef7" />
      <div className="px-5 py-7">
        <h3 className="typo-14 font-bold text-222222 mb-4">준비물</h3>
        <p className="typo-13 font-semibold text-[#908E9E]">
          {preparation.join(', ')}
        </p>
      </div>
    </section>
  );
}
